/**
 * Logical sound id -> clip data, for `SoundManager.load()`.
 *
 * The 28 clips under `apps/client/public/assets/gen/audio/` are Stable Audio Open
 * generations, peak-normalised by the host wrapper (see ASSETS.md's "Audio notes"). That
 * means a 0.12 s UI tick and a 0.90 s rifle shot arrive at the same peak level — so every
 * `gain` below is a deliberate runtime mixing decision, not a guess. Values were chosen by
 * ear-balancing category against category (weapons > vox > footsteps > ambience beds >
 * UI), not against the source files' own loudness.
 */

/** Logical sound ids the rest of the game refers to. */
export type SoundId =
  | 'carbine_shot'
  | 'sidearm_shot'
  | 'reload'
  | 'footstep_regolith'
  | 'footstep_metal'
  | 'chorister_voice'
  | 'cantor_charge'
  | 'sexton_drone'
  | 'beacon_hum'
  | 'dimming_wind'
  | 'dust_wind'
  | 'downs_bed'
  | 'lanternfall_bed'
  | 'rain_bed'
  | 'rain_hull'
  | 'ship_engine'
  | 'ship_wind'
  | 'hull_hit'
  | 'flak_pass'
  | 'ship_alarm'
  | 'touchdown'
  | 'ui_tick'
  | 'ui_confirm'
  | 'hit_marker'
  | 'loot_pickup';

export interface SoundDef {
  /** One clip, or several for round-robin variants (footsteps, chorister vox). */
  urls: readonly string[];
  /** Base mix gain, 0..1 — see the file header. Multiplied by bus and master gain. */
  gain: number;
  /** Fractional random pitch jitter applied per play, e.g. 0.03 = +-3%. 0/omit = none. */
  pitchJitter?: number;
  /** Minimum ms between two plays of this id — the machine-gun-stacking guard. 0/omit = none. */
  cooldownMs?: number;
  /** True for beds meant to be started with `startLoop()` and never with `play()`. */
  loop?: boolean;
}

const AUDIO_BASE = '/assets/gen/audio';

export const SOUND_MANIFEST: Record<SoundId, SoundDef> = {
  carbine_shot: {
    urls: [`${AUDIO_BASE}/sfx-carbine-shot.wav`],
    gain: 0.85,
    pitchJitter: 0.03,
    cooldownMs: 30,
  },
  sidearm_shot: {
    urls: [`${AUDIO_BASE}/sfx-sidearm-shot.wav`],
    gain: 0.8,
    pitchJitter: 0.03,
    cooldownMs: 30,
  },
  reload: {
    urls: [`${AUDIO_BASE}/sfx-reload.wav`],
    gain: 0.7,
    pitchJitter: 0.02,
    cooldownMs: 200,
  },
  footstep_regolith: {
    urls: [
      `${AUDIO_BASE}/sfx-step-regolith-1.wav`,
      `${AUDIO_BASE}/sfx-step-regolith-2.wav`,
      `${AUDIO_BASE}/sfx-step-regolith-3.wav`,
    ],
    gain: 0.42,
    pitchJitter: 0.03,
  },
  footstep_metal: {
    urls: [
      `${AUDIO_BASE}/sfx-step-hull-1.wav`,
      `${AUDIO_BASE}/sfx-step-hull-2.wav`,
      `${AUDIO_BASE}/sfx-step-hull-3.wav`,
    ],
    gain: 0.48,
    pitchJitter: 0.03,
  },
  chorister_voice: {
    urls: [`${AUDIO_BASE}/vox-chorister-1.wav`, `${AUDIO_BASE}/vox-chorister-2.wav`],
    gain: 0.8,
    pitchJitter: 0.02,
    cooldownMs: 1500,
  },
  cantor_charge: {
    urls: [`${AUDIO_BASE}/sfx-cantor-charge.wav`],
    gain: 0.85,
    cooldownMs: 500,
  },
  sexton_drone: {
    urls: [`${AUDIO_BASE}/amb-sexton-drone.wav`],
    gain: 0.55,
    loop: true,
  },
  beacon_hum: {
    urls: [`${AUDIO_BASE}/amb-extract-beacon.wav`],
    gain: 0.32,
    loop: true,
  },
  dimming_wind: {
    urls: [`${AUDIO_BASE}/amb-dimming-gust.wav`],
    gain: 0.5,
    loop: true,
  },
  /**
   * The Spine's weather beds. All three are long (16-20 s) so the loop point is hard to hear,
   * and all three sit *under* the Dimming's gust in the mix: weather is the room you are in, not
   * an event, and a bed you notice is a bed that is too loud. `SoundManager.setWeather` fades
   * them in and out and rides `dust_wind`'s gain for the gusts, so these are base levels only.
   */
  dust_wind: {
    urls: [`${AUDIO_BASE}/amb-dust-wind.wav`],
    gain: 0.42,
    loop: true,
  },
  rain_bed: {
    urls: [`${AUDIO_BASE}/amb-rain-bed.wav`],
    gain: 0.38,
    loop: true,
  },
  /**
   * The two zone beds of the pivot (`tools/assetgen/src/downs-beds.ts`): the floor the Vigil
   * Downs and Lanternfall each sit on, one per map, faded across by `SoundManager.setZoneBed`.
   *
   * Both are 45 s and genuinely seam-crossfaded — the first clips in this deck that are, which is
   * why they can be this short without the loop point announcing itself every twenty seconds.
   * They are also deliberately the quietest beds in the manifest: the Downs' bed carries a
   * distant choral swell 15 dB under its own wind, because the Choir the player is *meant* to
   * hear is `render/life.ts`'s spatial camp loops, and a bed loud enough to compete with those
   * would flatten the whole "audible before visible" idea into a constant.
   */
  downs_bed: {
    urls: [`${AUDIO_BASE}/amb-downs-bed.wav`],
    gain: 0.4,
    loop: true,
  },
  /** The city: the same wind, filtered darker and quieter, with a slow bell under it. No voices —
   *  Lanternfall is the one place on the map the Choir is not. */
  lanternfall_bed: {
    urls: [`${AUDIO_BASE}/amb-lanternfall-bed.wav`],
    gain: 0.46,
    loop: true,
  },
  /** Drips on plating. Only audible near the hull — see `SoundManager.setNearHull`. */
  rain_hull: {
    urls: [`${AUDIO_BASE}/amb-rain-hull.wav`],
    gain: 0.34,
    loop: true,
  },
  /**
   * The dropship deck (`game/insertion.ts`). Both beds are 16 s for the same reason the weather
   * beds are 16–20 s: there is no ffmpeg on the generation host to crossfade a seam, so the loop
   * is made long enough that the seam lands once in a twenty-five-second flight instead of four
   * times. The engine is the loudest bed in the game on purpose — you are sitting in it — and
   * `Insertion` ducks it under every pilot line rather than mixing it quiet all the way through.
   */
  ship_engine: {
    urls: [`${AUDIO_BASE}/amb-ship-engine.wav`],
    gain: 0.62,
    loop: true,
  },
  /** Open-door slipstream. Quieter source than the engine (−19.6 dB RMS to its −11.5), so the
   *  base gain is higher to land beside it rather than under it. */
  ship_wind: {
    urls: [`${AUDIO_BASE}/amb-ship-wind.wav`],
    gain: 0.75,
    loop: true,
  },
  /**
   * A round finding the airframe. Three variants round-robin because at the thickest part of the
   * run the ship is hit every second or so and one clip repeated at that rate reads as a bug.
   * The cooldown is short: this *should* be able to double-tap.
   */
  hull_hit: {
    urls: [
      `${AUDIO_BASE}/sfx-hull-hit-1.wav`,
      `${AUDIO_BASE}/sfx-hull-hit-2.wav`,
      `${AUDIO_BASE}/sfx-hull-hit-3.wav`,
    ],
    gain: 0.8,
    pitchJitter: 0.07,
    cooldownMs: 45,
  },
  /**
   * A tracer whipping past the door. `FIRE_INTERVAL_MIN` is 0.085 s, so without a cooldown this
   * would be a buzzsaw; 170 ms keeps the near-misses as punctuation.
   */
  flak_pass: {
    urls: [`${AUDIO_BASE}/sfx-flak-pass-1.wav`, `${AUDIO_BASE}/sfx-flak-pass-2.wav`],
    gain: 0.55,
    pitchJitter: 0.09,
    cooldownMs: 170,
  },
  /** One klaxon blip, fired with the bay lamp's red strobe and never on its own. */
  ship_alarm: {
    urls: [`${AUDIO_BASE}/sfx-ship-alarm.wav`],
    gain: 0.5,
    cooldownMs: 900,
  },
  /** Skids down: hydraulic thump and the dust it throws. Once per flight. */
  touchdown: {
    urls: [`${AUDIO_BASE}/sfx-touchdown.wav`],
    gain: 0.9,
    cooldownMs: 1000,
  },
  ui_tick: {
    urls: [`${AUDIO_BASE}/ui-tick.wav`],
    gain: 0.55,
    cooldownMs: 30,
  },
  ui_confirm: {
    urls: [`${AUDIO_BASE}/ui-confirm.wav`],
    gain: 0.65,
    cooldownMs: 60,
  },
  hit_marker: {
    urls: [`${AUDIO_BASE}/ui-hitmarker.wav`],
    gain: 0.5,
    cooldownMs: 40,
  },
  loot_pickup: {
    urls: [`${AUDIO_BASE}/ui-loot-pickup.wav`],
    gain: 0.6,
    cooldownMs: 80,
  },
};
